import axios from 'axios';

export interface UserData {
  id?: number;
  username: string;
  password?: string;
  email: string;
  roles: string[];
  status: boolean | string; // Can be boolean or string for flexibility
  schoolId?: number;
  createdAt?: string;
  // Note: Backend may return a 'role' field, but we should never send it back
}

// Helper function to clean user data and remove duplicate 'role' field
const cleanUserData = (user: any): UserData => {
  const { role, ...cleanUser } = user; // Destructure 'role' out to remove it, keep only 'roles'
  return cleanUser;
};

const API_URL = `${process.env.REACT_APP_API_URL}/admin/users`;

// Get all users - calls /admin/users/getAll
export const getAllUsers = async (): Promise<UserData[]> => {
  try {
    const response = await axios.get(`${API_URL}/getAll`);
    // Clean user data to remove duplicate 'role' field
    const cleanedUsers = response.data.map((user: any) => cleanUserData(user));
    return cleanedUsers;
  } catch (error) {
    throw error;
  }
};

// Get users by role - calls /admin/users?role=ROLE
export const getUsersByRole = async (role: string): Promise<UserData[]> => {
  try {
    const response = await axios.get(API_URL, {
      params: { role }
    });
    // Clean user data to remove duplicate 'role' field
    const cleanedUsers = response.data.map((user: any) => cleanUserData(user));
    return cleanedUsers;
  } catch (error) {
    throw error;
  }
};

export const createUser = async (userData: UserData): Promise<UserData> => {
  try {
    // Create clean payload - only send fields that backend expects
    const payload = {
      username: userData.username,
      email: userData.email,
      password: userData.password,
      roles: userData.roles,
      status: userData.status,
      schoolId: userData.schoolId
    };
    const response = await axios.post(API_URL, payload);
    // Clean response data to remove duplicate 'role' field
    return cleanUserData(response.data);
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (id: number, userData: UserData): Promise<UserData> => {
  try {
    // Create clean payload - only send fields that backend expects (matching backend DTO)
    const payload = {
      username: userData.username,
      email: userData.email,
      roles: userData.roles,     // Only send 'roles' array, never 'role'
      status: userData.status,
      schoolId: userData.schoolId
    };
    // Add password only if provided (for password updates)
    if (userData.password) {
      (payload as any).password = userData.password;
    }
    const response = await axios.put(`${API_URL}/${id}`, payload);
    // Clean response data to remove duplicate 'role' field
    return cleanUserData(response.data);
  } catch (error) {
    throw error;
  }
};

export const deleteUser = async (id: number): Promise<void> => {
  try {
    await axios.delete(`${API_URL}/${id}`);
  } catch (error) {
    throw error;
  }
};
